/**
 * Showroom Footer Banner Component
 *
 * Displays the nearest showroom information in the footer.
 * Uses geolocation or default location to find the nearest store.
 * Text strings are managed via translation bundles.
 *
 * Caching Strategy:
 * - Nearest showroom is cached in a region-specific cookie
 * - Geolocation-based results: 1 hour TTL (user might move)
 * - Default location results: 24 hour TTL (won't change)
 */

import React, {useCallback, useEffect, useMemo, useState} from 'react';
import {FormattedMessage, useIntl} from 'react-intl';

import {useSearchStores} from '@salesforce/commerce-sdk-react';
import {getAssetUrl} from '@salesforce/pwa-kit-react-sdk/ssr/universal/utils';
import {
  Box, Button, Flex, HStack, Skeleton, Text, useMultiStyleConfig, VStack,
} from '@salesforce/retail-react-app/app/components/shared/ui';
import useMultiSite from '@salesforce/retail-react-app/app/hooks/use-multi-site';
import useNavigation from '@salesforce/retail-react-app/app/hooks/use-navigation';

import {useShowroomDetailss} from '@amblique/contentful/app/contentful/hooks';
import {LocationPinIcon} from '@amblique/king-living/app/components/icons';
import {Newsletter} from '@amblique/king-living/app/components/newsletter';
import {
  getSiteConfig,
  FALLBACK_STORE_SEARCH_RADIUS,
} from '@amblique/king-living/app/constants/site-config';
import {generateSlug, getNearestShowroomCookieName} from '@amblique/king-living/app/utils/utils';

// Cache TTL constants (in seconds)
const GEOLOCATION_CACHE_TTL = 60 * 60; // 1 hour
const DEFAULT_LOCATION_CACHE_TTL = 24 * 60 * 60; // 24 hours

/**
 * Get nearest showroom from cookie
 * @param {string} cookieName - Site-specific cookie name
 * @returns {Object|null} Cached showroom data or null if expired/not found
 */
const getNearestShowroomFromCookie = (cookieName) => {
  if (typeof document === 'undefined' || !cookieName) return null;

  try {
    const cookie = document.cookie
      .split('; ')
      .find((row) => row.startsWith(`${cookieName}=`));

    if (cookie) {
      const value = cookie.split('=')[1];

      return JSON.parse(decodeURIComponent(value));
    }
  } catch (error) {
    console.error('Error reading nearest showroom cookie:', error);
  }

  return null;
};

/**
 * Save nearest showroom to cookie with expiry
 * @param {string} cookieName - Site-specific cookie name
 * @param {Object} showroom - Showroom data to cache
 * @param {number} maxAge - Cookie max-age in seconds
 */
const saveNearestShowroomToCookie = (cookieName, showroom, maxAge) => {
  if (typeof document === 'undefined' || !cookieName || !showroom) return;

  try {
    const value = encodeURIComponent(JSON.stringify(showroom));

    document.cookie = `${cookieName}=${value}; max-age=${maxAge}; path=/; SameSite=Lax`;
  } catch (error) {
    console.error('Error saving nearest showroom cookie:', error);
  }
};

/**
 * Showroom Footer Banner
 *
 * Displays the "Visit your nearest King showroom" banner in the footer.
 * Used across all pages to show the nearest showroom based on user location.
 */
export const ShowroomFooterBanner = ({...props}) => {
  const styles = useMultiStyleConfig('Showroom', {});
  const navigate = useNavigation();
  const {formatMessage} = useIntl();
  const {site} = useMultiSite();

  // Get site configuration
  const siteConfig = getSiteConfig(site?.id);
  const defaultLocation = siteConfig.defaultLocation;
  const nearestShowroomCookieName = getNearestShowroomCookieName(site?.id);

  // State
  const [nearestStore, setNearestStore] = useState(null);
  const [userLocation, setUserLocation] = useState(null);
  const [isLocationLoading, setIsLocationLoading] = useState(true);
  const [hasAttemptedLocation, setHasAttemptedLocation] = useState(false);
  const [isUsingGeolocation, setIsUsingGeolocation] = useState(false);

  // Determine search coordinates (user location or default)
  const searchCoords = useMemo(() => {
    if (userLocation) {
      return userLocation;
    }
    if (defaultLocation) {
      return {
        latitude: defaultLocation.lat,
        longitude: defaultLocation.lng,
      };
    }

    return null;
  }, [userLocation, defaultLocation]);

  // Always use FALLBACK_STORE_SEARCH_RADIUS for footer banner
  // This ensures we always find the nearest store regardless of distance
  const searchRadius = FALLBACK_STORE_SEARCH_RADIUS;

  // Fetch stores from SFCC API
  const {
    data: storesData,
    isLoading: isStoresLoading,
  } = useSearchStores(
    {
      parameters: {
        latitude: searchCoords?.latitude,
        longitude: searchCoords?.longitude,
        maxDistance: searchRadius,
        distanceUnit: siteConfig.distanceUnit || 'km',
        limit: 1,
        locale: siteConfig.defaultLocale || 'en-AU',
      },
    },
    {
      enabled: !!searchCoords && hasAttemptedLocation && !nearestStore,
    }
  );

  // Check for cached nearest showroom and get location on mount
  useEffect(() => {
    // First check for cached nearest showroom in cookie
    const cachedShowroom = getNearestShowroomFromCookie(nearestShowroomCookieName);

    if (cachedShowroom) {
      setNearestStore(cachedShowroom);
      setIsLocationLoading(false);
      setHasAttemptedLocation(true);

      return;
    }

    // No cache - try browser geolocation
    if (typeof navigator !== 'undefined' && navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          setUserLocation({
            latitude: position.coords.latitude,
            longitude: position.coords.longitude,
          });
          setIsUsingGeolocation(true);
          setIsLocationLoading(false);
          setHasAttemptedLocation(true);
        },
        () => {
          // Geolocation failed/denied, use default location
          setIsUsingGeolocation(false);
          setIsLocationLoading(false);
          setHasAttemptedLocation(true);
        },
        {
          enableHighAccuracy: false,
          timeout: 5000,
          maximumAge: 300000, // 5 minutes
        }
      );
    } else {
      // Geolocation not available, use default
      setIsUsingGeolocation(false);
      setIsLocationLoading(false);
      setHasAttemptedLocation(true);
    }
  }, [nearestShowroomCookieName]);

  // Update nearest store when API returns data and cache it
  useEffect(() => {
    if (storesData?.data?.[0] && !nearestStore) {
      const store = storesData.data[0];
      const showroomData = {
        id: store.id,
        name: store.name,
        slug: generateSlug(store.name),
        stateCode: store.stateCode,
        city: store.city,
      };

      setNearestStore(showroomData);

      // Cache the result with appropriate TTL
      const cacheTTL = isUsingGeolocation ? GEOLOCATION_CACHE_TTL : DEFAULT_LOCATION_CACHE_TTL;

      saveNearestShowroomToCookie(nearestShowroomCookieName, showroomData, cacheTTL);
    }
  }, [storesData, nearestStore, nearestShowroomCookieName, isUsingGeolocation]);

  // Fetch ShowroomDetails from Contentful by storeId to get featuredImage
  const {
    data: showroomDetailsCollection,
  } = useShowroomDetailss({
    variant: 'default', // Need 1-level deep to get featuredImage
    variables: {
      where: {storeId: nearestStore?.id},
      limit: 1,
    },
    queryOptions: {
      enabled: !!nearestStore?.id,
    },
  });

  // Extract showroom details and featured image
  const showroomDetails = showroomDetailsCollection?.items?.[0];
  const featuredImage = showroomDetails?.featuredImage;
  const featuredImageUrl = featuredImage?.desktopAsset?.url ||
    featuredImage?.tabletAsset?.url ||
    featuredImage?.mobileAsset?.url;

  // Get showroom display info
  const showroomName = nearestStore?.name || '';
  const showroomState = nearestStore?.stateCode || nearestStore?.city || '';
  const showroomSlug = nearestStore?.slug || (nearestStore?.name ? generateSlug(nearestStore.name) : null);

  // Handle primary button click (Visit nearest showroom)
  const handleVisitShowroom = useCallback(() => {
    if (showroomSlug) {
      navigate(`/showroom/${showroomSlug}`);
    } else {
      navigate('/showrooms');
    }
  }, [navigate, showroomSlug]);

  // Handle secondary button click (Find more showrooms)
  const handleFindMoreShowrooms = useCallback(() => {
    navigate('/showrooms');
  }, [navigate]);

  // Background image - prefer Contentful featuredImage, fallback to static asset
  const fallbackBackgroundImage = getAssetUrl('static/img/showroom-bg.png');
  const backgroundImage = featuredImageUrl || fallbackBackgroundImage;

  // Loading state
  const isLoading = isLocationLoading || (isStoresLoading && !nearestStore);

  if (isLoading) {
    return (
      <Box sx={styles.container} {...props}>
        <Skeleton height="300px" width="100%" />
      </Box>
    );
  }

  return (
    <Box sx={styles.container} {...props} backgroundImage={`url('${backgroundImage}')`}>
      <Flex sx={styles.content}>
        {/* Left Section - Showroom Content */}
        <VStack
          align="start"
          spacing={{base: 6, lg: 10}}
          sx={styles.leftSection}
        >
          {/* Header Section */}
          <VStack align="start" spacing={5}>
            <Text sx={styles.heading}>
              <FormattedMessage
                defaultMessage="Visit your nearest King showroom"
                id="footer_showroom.heading"
              />
            </Text>

            {/* Location Info - Only show if we have a nearest store */}
            {showroomName && (
              <HStack spacing={1.5}>
                <LocationPinIcon boxSize={5} color="white" />
                <Text sx={styles.locationText}>
                  <FormattedMessage
                    defaultMessage="Your closest showroom is {showroomName}, {showroomState}"
                    id="footer_showroom.closest_showroom"
                    values={{
                      showroomName,
                      showroomState,
                    }}
                  />
                </Text>
              </HStack>
            )}
          </VStack>

          {/* Action Buttons */}
          <Flex
            direction={{base: 'column', lg: 'row'}}
            gap={{base: 2, lg: 4}}
            width="100%"
          >
            <Button
              size="lg"
              sx={styles.primaryButton}
              onClick={handleVisitShowroom}
            >
              {formatMessage({
                defaultMessage: 'Visit nearest showroom',
                id: 'footer_showroom.button.visit_nearest',
              })}
            </Button>

            <Button
              size="lg"
              sx={styles.secondaryButton}
              variant="outline"
              onClick={handleFindMoreShowrooms}
            >
              {formatMessage({
                defaultMessage: 'Find more showrooms',
                id: 'footer_showroom.button.find_more',
              })}
            </Button>
          </Flex>
        </VStack>

        {/* Right Section - Newsletter (mock data for now) */}
        <Box sx={styles.rightSection}>
          <Newsletter />
        </Box>
      </Flex>
    </Box>
  );
};

// Keep backward compatibility with old name
export const Showroom = ShowroomFooterBanner;

export default ShowroomFooterBanner;
